
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import Logo from './Logo';
import { trackEvent } from './analytics';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const previousOverflow = document.body.style.overflow;
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'eAIP', path: '/modules/eaip' },
    {
      name: 'SMS',
      path: '/modules/sms',
      children: [{ name: 'SMS Features', path: '/modules/sms/features' }],
    },
    {
      name: 'LMS',
      path: '/modules/lms',
      children: [
        { name: 'ANSP & CAA', path: '/lms-ansp-caa' },
        { name: 'Airlines', path: '/lms-airlines' },
        { name: 'Aviation Companies', path: '/lms-aviation-companies' },
      ],
    },
    { name: 'PANS-OPS', path: '/modules/pans-ops' },
    { name: 'UTM', path: '/modules/utm' },
    { name: 'Compliance', path: '/compliance' },
    { name: 'Aviation Insight', path: '/blog' },
    { name: 'News', path: '/news' },
    { name: 'FlyMate', path: '/flymate' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <Link to="/">
              <Logo size="sm" />
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              link.children ? (
                <div key={link.path} className="relative group">
                  <Link
                    to={link.path}
                    className={`text-sm font-medium transition-colors ${
                      isActive(link.path) ? 'text-accent' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {link.name}
                  </Link>
                  <div className="absolute left-0 mt-3 w-56 rounded-xl border border-white/10 bg-primary/95 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <div className="py-2">
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            isActive(child.path) ? 'text-accent' : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors ${
                    isActive(link.path) ? 'text-accent' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link
              to="/contact"
              className="bg-accent/10 text-accent hover:bg-accent hover:text-primary px-5 py-2.5 rounded-full text-sm font-bold transition-all neon-border"
              onClick={() => trackEvent('demo_request', { form_id: 'nav_request_demo' })}
            >
              Request Demo
            </Link>
          </div>

          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-expanded={isOpen}
              aria-controls="mobile-navigation"
              aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
              className="text-slate-400 hover:text-white p-2"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div
          id="mobile-navigation"
          className="md:hidden border-b border-white/10 bg-primary/95 backdrop-blur animate-fade-in-up"
        >
          <div className="max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain px-4 pt-2 pb-6 space-y-1">
            {navLinks.map((link) => (
              <div key={link.path}>
                <Link
                  to={link.path}
                  className={`block px-3 py-4 text-base font-medium rounded-md ${
                    isActive(link.path) ? 'text-accent bg-accent/5' : 'text-slate-400'
                  }`}
                >
                  {link.name}
                </Link>
                {link.children && (
                  <div className="pl-4">
                    {link.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={`block px-3 py-2 text-sm rounded-md ${
                          isActive(child.path) ? 'text-accent bg-accent/5' : 'text-slate-500'
                        }`}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/contact"
              onClick={() => {
                trackEvent('demo_request', { form_id: 'nav_request_demo' });
              }}
              className="block w-full text-center bg-accent text-primary px-3 py-4 rounded-md text-base font-bold mt-4"
            >
              Request Demo
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
